import React, { useState, useEffect, useCallback } from "react";
import { Table, Input, Button, Select, Spin, Progress, Popover } from "antd";
import editIcon from "../../../Assets/svg/table-action-edit.svg";
import deleteIcon from "../../../Assets/svg/table-action-delete.svg";
import previewIcon from "../../../Assets/svg/table-action-preview.svg";
import EditModal from "./modals/EditModal";
import PreviewModal from "./modals/PreviewModal";
import { httpService } from "../../../services/httpService.service";
import DeleteComponent from "../../components/DeleteComponent/DeleteComponent";
import SearchIcon from "../../../Assets/svg/searchIcon.svg";
import MessagePopUp from "../../components/Messages/MessagePopUp";
import { RolesEnum } from "../../../services/rolesEnum";
import { isAuthorized } from "../../../services/authService";
import { debounce } from "lodash";
import { years, months } from "../../../services/Enums/monthsAndYears";
import { getIpStatus, ipStatus } from "../../../services/Enums/ipPlanEnums";
import {
  getIpCompleteness,
  getCompletenessColor,
  stageBandLabel,
} from "../../../services/helpFunctions/ipCompleteness";
import { getProjectIPCompleteness } from "../../../services/features/ipPlan.service";
const { Option } = Select;

const PlanningTableView = ({ ipPlansData, onDataLoaded }) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isPreviewModalVisible, setPreviewModalVisible] = useState(false);
  const [isEditModalVisible, setEditModalVisible] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [data, setData] = useState([]);
  const [users, setUsers] = useState([]);
  // F-07 — authoritative completeness from the backend, keyed by IP plan id.
  const [completenessMap, setCompletenessMap] = useState(new Map());
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 576);

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth > 576);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [message, setMessage] = useState({
    trigger: false,
    type: "",
    icon: "",
    title: "",
    text: "",
  });

  const [loading, setLoading] = useState(true);
  const projectID = localStorage.getItem("currentProjectID");
  const [showFilters, setShowFilters] = useState(false);
  const [implementingPartners, setImplementingPartners] = useState([]);
  const [filtersValue, setFiltersValue] = useState({
    ImplementingPartnerId: undefined,
    month: undefined,
    year: undefined,
    IPStatus: undefined,
    AssignedTo: undefined,
    Search: "",
  });
  const [filters, setFilters] = useState({
    ImplementingPartnerId: undefined,
    month: undefined,
    year: undefined,
    IPStatus: undefined,
    AssignedTo: undefined,
    Search: "",
  });

  useEffect(() => {
    const fetchImplementingPartners = async () => {
      const workspaceId = localStorage.getItem("currentContractId");
      const apiIps = `/api/ImplementingPartner/getAllImplementingPartners?contractId=${workspaceId}&IgnorePagination=true`;

      httpService.get(
        apiIps,
        (res) => setImplementingPartners(res.data)
        // (err) => console.log(err)
      );
    };

    fetchImplementingPartners();

    const workspaceId = localStorage.getItem("currentContractId");

    httpService.get(
      `/api/User/getAllusers?RoleIds=e78550f4-c017-4ec7-94e8-e040090c32b1&RoleIds=615a8dc6-ca07-4f82-8d0d-7d82dd4d3ef8&pageSize=1000&ContractIds=${workspaceId}&IgnorePagination=true`,

      (res) => setUsers(res.data)
      // (err) => console.log(err)
    );
  }, []);

  function isUUID(str) {
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  }

  const itemToPreview = localStorage.getItem("entityId");
  useEffect(() => {
    if (itemToPreview && isUUID(itemToPreview)) {
      setCurrentRecord(itemToPreview);
      setPreviewModalVisible(true);
      setTimeout(() => {
        localStorage.setItem("entityId", "");
      }, 300);
    }
  }, [itemToPreview]);

  const handleClearFilters = () => {
    setFilters({
      ImplementingPartnerId: undefined,
      month: undefined,
      year: undefined,
      IPStatus: undefined,
      AssignedTo: undefined,
      Search: "",
    });
    setFiltersValue({
      ImplementingPartnerId: undefined,
      month: undefined,
      year: undefined,
      IPStatus: undefined,
      AssignedTo: undefined,
      Search: "",
    });
  };
  const handleFilterChange = (type, value) => {
    // useState setters take a single value — set the specific key immutably,
    // otherwise filtersValue was being clobbered to the bare `type` string and
    // the controlled Search/filter inputs lost their displayed values.
    setFiltersValue((prev) => ({ ...prev, [type]: value }));
    debouncedHandler({ [type]: value });

    setPagination((prevPagination) => ({ ...prevPagination, current: 1 }));
  };

  const debouncedHandler = useCallback(
    debounce((filter) => {
      setFilters((prevFilters) => ({
        ...prevFilters,
        ...filter,
      }));
      setPagination((prevPagination) => ({ ...prevPagination, current: 1 }));
    }, 500),
    []
  );

  const handleSearchChange = (e) => {
    const search = e.target.value;
    handleFilterChange("Search", search);
  };

  const handleToggleFilters = () => {
    setShowFilters(!showFilters);
  };

  useEffect(() => {
    fetchData();
  }, [filters, pagination.current, pagination.pageSize]);

  // F-07 — load the dedicated completeness scores once per project and key
  // them by IP plan id so the column can show the authoritative % + breakdown.
  useEffect(() => {
    if (!projectID) return;
    getProjectIPCompleteness(
      projectID,
      (res) => {
        const list = res?.data || res || [];
        const map = new Map();
        (Array.isArray(list) ? list : []).forEach((it) => {
          map.set(it.iPPlanId, it);
        });
        setCompletenessMap(map);
      },
      () => {}
    );
  }, [projectID]);

  const fetchData = async () => {
    let apiUrl = `/api/IPPlan/getAllIPPlans?ProjectId=${projectID}`;
    for (const [key, value] of Object.entries(filters)) {
      if (value) {
        apiUrl += `&${key}=${value}`;
      }
    }

    apiUrl += `&pageNumber=${pagination.current}&pageSize=${pagination.pageSize}`;

    setLoading(true);
    httpService.get(
      apiUrl,
      (response) => {
        const formattedData = response.data.map((item) => ({
          key: item.id,
          // F-07 — completeness computed from existing fields (read-only).
          completeness: getIpCompleteness(item),
          ipplan: item?.identifier,
          ipPlanCode: item?.ipPlanCode || "N/A",
          ipplanid: item?.id,
          ip: item.implementingPartner?.name || "N/A",
          implementingPartner: item.implementingPartner?.id || "N/A",
          contractId: item.implementingPartner?.contractId || "N/A",
          assignedTo:
            item.assignee?.userDetails?.firstName &&
            item.assignee?.userDetails?.lastName
              ? `${item.assignee.userDetails.firstName} ${item.assignee.userDetails.lastName}`
              : item.assignee?.userName || "N/A",
          clusters:
            item.clusters?.map((cluster) => cluster.name).join(", ") || "N/A",
          initialMeeting: item.initialMeeting || "N/A",
          generalInfo: item.generalInfo || "N/A",
          office: item.office?.name || "N/A",
          month: item?.planningRound?.month || "N/A",
          year: item?.planningRound?.year || "N/A",
          programDescriptions: item?.planningRound?.programDescription || "N/A",
          pointOfContactName: item?.pointOfContact?.name || "N/A",
          pointOfContactRole: item?.pointOfContact?.role || "N/A",
          pointOfContactEmail: item?.pointOfContact?.email || "N/A",
          pointOfContactPhone: item?.pointOfContact?.phone || "N/A",
          pointOfContactOrganization:
            item?.pointOfContact?.organization || "N/A",
          securityPlan: item.securityPlan || "N/A",
          postponed: item.isPostponed ? "Yes" : "No",
          reasonForPostponement: item.reasonOfPostponed || "N/A",
          resources: item.resources || "N/A",
          currentIpStatus: item?.currentIPStatus || "N/A",
          ipDonorReportingStatus: item?.ipDonorReportingEnum || "N/A",
          projectName: item.project?.projectName || "N/A",
          projectNumber: item.project?.projectNumber || "N/A",
          ipPlanCities:
            item.ipPlanCities?.map((city) => city.name).join(", ") || "N/A",
          ipPlanStates:
            item.ipPlanStates?.map((state) => state.stateName).join(", ") ||
            "N/A",
        }));

        setData(formattedData);
        setPagination((prevPagination) => ({
          ...prevPagination,
          current: response.metaData.pageNumber,
          pageSize: response.metaData.pageSize,
          total: response.metaData.totalItemCount,
        }));

        setLoading(false);
      },
      (error) => {
        setLoading(false);
      }
    );
  };

  const handleTableChange = (pagination) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      current: pagination.current,
      pageSize: pagination.pageSize,
    }));
  };

  useEffect(() => {
    fetchData(ipPlansData, pagination.current, pagination.pageSize);
  }, [ipPlansData]);

  const handleDelete = (record) => {
    setCurrentRecord(record.key); // Set the current record to be deleted
    setShowDeleteModal(true); // Show the delete modal
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    if (currentRecord) {
      const requestBody = [
        {
          id: currentRecord,
          isDeleted: true,
        },
      ];

      const url = `/api/IPPlan/deleteIPPlans`;

      httpService.delete(
        url,
        requestBody,
        (response) => {
          const message =
            response.message ||
            "IP plan was deleted successfully from your database!";
          setMessage({
            trigger: true,
            type: "success",
            icon: deleteIcon,
            title: "IP plan deleted successfully!",
            text: message,
          });
          setShowDeleteModal(false);
          setCurrentRecord(null);
          fetchData();
          setTimeout(() => {
            setMessage({
              trigger: false,
            });
          }, 3000);
          setLoading(false);
        },
        (error) => {
          const errorMessage =
            error?.errors[0]?.message ||
            "We had a problem deleting this ip plan, please try again.";

          setMessage({
            trigger: true,
            type: "danger",
            icon: deleteIcon,
            title: "Oops! A problem has occurred!",
            text: errorMessage,
          });
          setShowDeleteModal(false);
          setCurrentRecord(null);
          fetchData();
          setLoading(false);
          setTimeout(() => {
            setMessage({
              trigger: false,
            });
          }, 3000);
        }
      );
    }
  };

  const handlePreview = (record) => {
    setCurrentRecord(record?.key);
    setPreviewModalVisible(true);
  };

  const handleEdit = (record) => {
    setCurrentRecord(record);
    setEditModalVisible(true);
  };

  const handlePreviewOk = () => {
    setPreviewModalVisible(false);
  };

  const handleEditOk = () => {
    setEditModalVisible(false);
  };

  const handleCancel = () => {
    setPreviewModalVisible(false);
    setEditModalVisible(false);
  };

  const handleSelectionChange = (selectedRowKeys, selectedRows) => {
    setSelectedRowKeys(selectedRowKeys);
    onDataLoaded(selectedRows);
  };

  const rowSelection = {
    onChange: handleSelectionChange,
    selectedRowKeys,
  };

  const columns = [
    {
      title: <span className="styledTitle">IP Plan Identifier</span>,
      dataIndex: "ipplan",
      key: "ipplan",
      sorter: (a, b) => a.ipplan.localeCompare(b.ipplan),
      render: (text) => <span className="styledDataIndex3">{text}</span>,
    },

    {
      title: <span className="styledTitle text-nowrap">IP Project Code</span>,
      dataIndex: "ipPlanCode",
      key: "ipPlanCode",
      sorter: (a, b) => a.ipPlanCode.localeCompare(b.ipPlanCode),
      render: (text) => <span className="styledDataIndex3">{text}</span>,
    },
    {
      title: <span className="styledTitle">IP</span>,
      dataIndex: "ip",
      key: "ip",
      sorter: (a, b) => a.ip.localeCompare(b.ip),
      render: (text) => <span className="styledDataIndex3">{text}</span>,
    },
    {
      title: <span className="styledTitle">Assigned To</span>,
      dataIndex: "assignedTo",
      key: "assignedTo",
      sorter: (a, b) => a.assignedTo.localeCompare(b.assignedTo),
      render: (text) => <span className="styledDataIndex3">{text}</span>,
    },
    {
      title: <span className="styledTitle">Clusters</span>,
      dataIndex: "clusters",
      key: "clusters",
      sorter: (a, b) => a.currentIpStatus - b.currentIpStatus,
      render: (text) => <span className="styledDataIndex3">{text}</span>,
    },
    {
      title: <span className="styledTitle">Current IP Status</span>,
      dataIndex: "currentIpStatus",
      key: "currentIpStatus",
      sorter: (a, b) => a.currentIpStatus - b.currentIpStatus,
      render: (text) => {
        const status = getIpStatus(text);

        return (
          <div className={status.className}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="8px"
              height="8px"
              viewBox="0 0 9 9"
            >
              <circle cx="4.5" cy="4.27344" r="4" fill={status.fill} />
            </svg>
            <span>{status.text}</span>
          </div>
        );
      },
    },
    {
      title: <span className="styledTitle">Completeness</span>,
      key: "completeness",
      dataIndex: "completeness",
      sorter: (a, b) => {
        const pa =
          completenessMap.get(a.ipplanid)?.percentage ??
          a.completeness?.percent ??
          0;
        const pb =
          completenessMap.get(b.ipplanid)?.percentage ??
          b.completeness?.percent ??
          0;
        return pa - pb;
      },
      render: (completeness, record) => {
        const backend = completenessMap.get(record.ipplanid);
        const percent = Math.round(
          backend?.percentage ?? completeness?.percent ?? 0
        );

        // Prefer the backend breakdown (required fields / visits / tracking /
        // flags); fall back to the read-only client component checklist.
        let breakdown;
        if (backend?.breakdown) {
          const b = backend.breakdown;
          const rows = [
            ["Required fields", b.requiredFieldsScore, b.requiredFieldsMax],
            ["Site visits", b.visitsScore, b.visitsMax],
            ["Tracking", b.trackingScore, b.trackingMax],
            ["Flags resolved", b.flagsScore, b.flagsMax],
          ];
          breakdown = (
            <div style={{ maxWidth: 260 }}>
              <div style={{ marginBottom: 8, fontSize: 12, color: "#707073" }}>
                Stage: <strong>{stageBandLabel(backend.stageBand)}</strong>
              </div>
              {rows.map(([label, score, max]) => (
                <div
                  key={label}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    fontSize: 12,
                    padding: "2px 0",
                  }}
                >
                  <span>{label}</span>
                  <span style={{ color: "#707073" }}>
                    {score ?? 0}/{max ?? 0}
                  </span>
                </div>
              ))}
            </div>
          );
        } else {
          breakdown = (
            <div style={{ maxWidth: 260 }}>
              <div style={{ marginBottom: 8, fontSize: 12, color: "#707073" }}>
                Stage: <strong>{completeness?.band}</strong> ·{" "}
                {completeness?.completeCount}/{completeness?.totalCount} complete
              </div>
              {completeness?.components?.map((c) => (
                <div
                  key={c.label}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 12,
                    padding: "2px 0",
                  }}
                >
                  <span style={{ color: c.complete ? "#0E9349" : "#D4380D" }}>
                    {c.complete ? "✓" : "✗"}
                  </span>
                  <span>{c.label}</span>
                </div>
              ))}
            </div>
          );
        }

        return (
          <Popover
            placement="left"
            title="Completeness breakdown"
            content={breakdown}
          >
            <div style={{ minWidth: 120, cursor: "pointer" }}>
              <Progress
                percent={percent}
                size="small"
                strokeColor={getCompletenessColor(percent)}
              />
            </div>
          </Popover>
        );
      },
    },
    {
      title: <span className="styledTitle"></span>,
      key: "action",

      fixed: isLargeScreen && data.length > 0 ? "right" : undefined,
      render: (text, record) => (
        <div className="actionDiv">
          {isAuthorized([
            RolesEnum.ProjectManager,
            RolesEnum.SystemAdmin,
            RolesEnum.FieldCoordinator,
            RolesEnum.FieldTeamLeader,
            RolesEnum.Guest,
            RolesEnum.FieldEnumerator,
          ]) && (
            <img
              src={previewIcon}
              onClick={() => handlePreview(record)}
              style={{ cursor: "pointer" }}
            ></img>
          )}
          {isAuthorized([
            RolesEnum.ProjectManager,
            RolesEnum.SystemAdmin,
            RolesEnum.FieldCoordinator,
            RolesEnum.FieldTeamLeader,
          ]) && (
            <img
              src={editIcon}
              onClick={() => handleEdit(record)}
              style={{ cursor: "pointer" }}
            />
          )}
          {isAuthorized([RolesEnum.ProjectManager, RolesEnum.SystemAdmin]) && (
            <img
              src={deleteIcon}
              style={{ cursor: "pointer" }}
              onClick={() => handleDelete(record)}
            ></img>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      {" "}
      <MessagePopUp
        trigger={message.trigger}
        type={message.type}
        icon={message.icon}
        messageTitle={message.title}
        messageText={message.text}
      />
      <div className="page-content">
        <div className="filtersWrapper my-5">
          <div className="containerFilters">
            <Input
              placeholder="Search..."
              name="search"
              value={filtersValue.Search}
              suffix={<img src={SearchIcon} alt="Search" />}
              onChange={handleSearchChange}
            />
            <Button className="clearButton" onClick={handleClearFilters}>
              Clear All
            </Button>
            <Button onClick={handleToggleFilters} className="toggleButton">
              {!showFilters ? "Show Filters" : "Hide Filters"}
            </Button>
          </div>
          {showFilters && (
            <div className="filtersContainer">
              <div className="filter">
                <p className="filterText">Implementing Partner</p>
                <Select
                  style={{ width: 200, marginBottom: 20 }}
                  placeholder="Select an IP"
                  allowClear
                  showSearch
                  onChange={(value) =>
                    handleFilterChange("ImplementingPartnerId", value)
                  }
                  value={filtersValue.ImplementingPartnerId}
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .startsWith(input.toLowerCase())
                  }
                >
                  {implementingPartners.map((implementingPartner) => (
                    <Option
                      key={implementingPartner.id}
                      value={implementingPartner.id}
                    >
                      {implementingPartner.name}
                    </Option>
                  ))}
                </Select>
              </div>
              <div className="filter">
                <p className="filterText">Current IP Status</p>
                <Select
                  style={{ width: 200, marginBottom: 20 }}
                  placeholder="Select an IP status"
                  onChange={(value) => handleFilterChange("IPStatus", value)}
                  value={filtersValue.IPStatus}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .startsWith(input.toLowerCase())
                  }
                >
                  {Object.entries(ipStatus)
                    .filter(([key]) => key !== "default")
                    .map(([key, value]) => (
                      <Option key={key} value={key}>
                        {value.text}
                      </Option>
                    ))}
                </Select>
              </div>
              <div className="filter">
                <p className="filterText">Assigned To:</p>
                <Select
                  style={{ width: 200, marginBottom: 20 }}
                  placeholder="Select a user"
                  onChange={(value) => handleFilterChange("AssignedTo", value)}
                  value={filtersValue.AssignedTo}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .startsWith(input.toLowerCase())
                  }
                >
                  {users &&
                    users.map((user) => (
                      <Option key={user.id} value={user.id}>
                        {user.userDetails?.firstName &&
                        user.userDetails?.lastName
                          ? `${user.userDetails.firstName} ${user.userDetails.lastName}`
                          : user.userName}
                      </Option>
                    ))}
                </Select>
              </div>
              <div className="filter">
                <p className="filterText">Month</p>
                <Select
                  style={{ width: 200, marginBottom: 20 }}
                  placeholder="Select a month"
                  onChange={(value) => handleFilterChange("month", value)}
                  value={filtersValue.month}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .startsWith(input.toLowerCase())
                  }
                >
                  {Object.entries(months).map(([key, value]) => (
                    <Option key={key} value={key}>
                      {value}
                    </Option>
                  ))}
                </Select>
              </div>

              <div className="filter">
                <p className="filterText">Year</p>

                <Select
                  style={{ width: 200, marginBottom: 20 }}
                  placeholder="Select a year"
                  onChange={(value) => handleFilterChange("year", value)}
                  value={filtersValue.year}
                  allowClear
                  showSearch
                  filterOption={(input, option) => {
                    const children = React.Children.toArray(
                      option.children
                    ).join("");
                    return children.toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {years.map((year) => (
                    <Option key={year} value={year}>
                      {year}
                    </Option>
                  ))}
                </Select>
              </div>
            </div>
          )}
        </div>
        <div
          className="w-100 my-3 d-flex justify-content-start gap-2"
          style={{
            color: "var(--2, #AA1A5F)",
            fontFamily: "Poppins",
            fontSize: "16px",
            fontStyle: "normal",
            fontWeight: "600",
            lineHeight: "24px",
          }}
        >
          {loading ? (
            <Spin className="me-2" size="small" spinning={loading} />
          ) : (
            `Total: ${pagination.total} IP Plans`
          )}
        </div>
        <Table
          rowSelection={data.length > 0 ? rowSelection : ""}
          columns={data?.length > 0 ? columns : []}
          dataSource={data}
          loading={loading}
          className="tableTPM"
          style={{ marginTop: "20px" }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
        />
        <PreviewModal
          visible={isPreviewModalVisible}
          onOk={handlePreviewOk}
          onCancel={handleCancel}
          record={currentRecord}
        />
        <EditModal
          visible={isEditModalVisible}
          onOk={handleEditOk}
          onClose={handleCancel}
          setMessage={setMessage}
          record={currentRecord?.key}
          onEdit={fetchData}
        />
        <DeleteComponent
          visible={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          text="You're about to delete this project. This action can’t be undone."
          header="Delete Project"
          onConfirm={handleConfirmDelete}
          loading={loading}
        />
      </div>
    </>
  );
};

export default PlanningTableView;
